import { useState, useEffect, useMemo } from 'react';
import { leadsAPI, inventoryAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Send, UserPlus, Trash2, Eye, Search, Filter, X, Pencil, MoreVertical, FileText, Download } from 'lucide-react';
import { BulkSelectCheckbox, BulkActionBar } from '@/components/BulkActions';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

const statusColors = {
  new: 'bg-blue-100 text-blue-800 border-blue-300',
  contacted: 'bg-amber-100 text-amber-800 border-amber-300',
  qualified: 'bg-purple-100 text-purple-800 border-purple-300',
  converted: 'bg-green-100 text-green-800 border-green-300',
  lost: 'bg-gray-100 text-gray-700 border-gray-300',
};

export default function AdminLeads() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [leads, setLeads] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(null);
  const [viewLead, setViewLead] = useState(null);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', phone: '', type: 'inquiry', message: '' });
  const [saving, setSaving] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', type: 'inquiry', message: '' });
  
  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selected, setSelected] = useState([]);
  const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const load = () => {
    setLoading(true);
    leadsAPI.list().then(r => { setLeads(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => {
    load();
    inventoryAPI.list({ limit: 100 }).then(r => setInventory((r.data.items || r.data || []).filter(i => i.status === 'Available'))).catch(() => {});
  }, []);

  // Filtered leads
  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          lead.name?.toLowerCase().includes(query) ||
          lead.email?.toLowerCase().includes(query) ||
          lead.phone?.toLowerCase().includes(query) ||
          lead.message?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      if (statusFilter !== 'all' && lead.status !== statusFilter) return false;
      if (typeFilter !== 'all' && lead.type !== typeFilter) return false;
      return true;
    });
  }, [leads, searchQuery, statusFilter, typeFilter]);

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setTypeFilter('all');
  };

  const hasActiveFilters = searchQuery || statusFilter !== 'all' || typeFilter !== 'all';

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await leadsAPI.create(form);
      toast.success('Lead added');
      setAddOpen(false);
      setForm({ name: '', email: '', phone: '', type: 'inquiry', message: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await leadsAPI.update(id, { status });
      setLeads(prev => prev.map(l => l.id === id ? { ...l, status } : l));
      toast.success('Updated');
    } catch { toast.error('Failed'); }
  };

  const handleSendInventory = async () => {
    if (selectedUnits.length === 0) { toast.error('Select at least one unit'); return; }
    setSaving(true);
    try {
      await leadsAPI.sendInventory(sendOpen.id, selectedUnits);
      toast.success(`${selectedUnits.length} unit(s) sent to ${sendOpen.email}`);
      setSendOpen(null);
      setSelectedUnits([]);
    } catch { toast.error('Failed to send'); }
    setSaving(false);
  };

  const handleConvert = async (lead) => {
    if (!window.confirm(`Convert "${lead.name}" to a client?`)) return;
    try {
      await leadsAPI.convert(lead.id);
      toast.success(`${lead.name} converted to client!`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Conversion failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await leadsAPI.delete(id);
      toast.success('Lead deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const openEditLead = (lead) => {
    setEditForm({ name: lead.name || '', email: lead.email || '', phone: lead.phone || '', type: lead.type || 'inquiry', message: lead.message || '' });
    setEditLead(lead);
  };

  const handleEditLead = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await leadsAPI.update(editLead.id, editForm);
      toast.success('Lead updated');
      setEditLead(null);
      load();
    } catch { toast.error('Failed to update lead'); }
    setSaving(false);
  };

  const toggleUnit = (id) => setSelectedUnits(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <div data-testid="admin-leads">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 uppercase">{isAdmin ? 'Leads' : 'My Leads'}</h1>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{filteredLeads.length} of {leads.length} leads</span>
          <Button className="bg-[#E31937] text-white hover:bg-[#C4122C]" onClick={() => setAddOpen(true)} data-testid="add-lead-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Lead
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search by name, email, phone, message..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9 text-sm" data-testid="search-input" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="status-filter">
              <Filter className="w-3 h-3 mr-1.5 text-gray-400" /><SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="qualified">Qualified</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
              <SelectItem value="lost">Lost</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="type-filter">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="inquiry">Inquiry</SelectItem>
              <SelectItem value="viewing">Viewing Request</SelectItem>
              <SelectItem value="financing">Financing</SelectItem>
              <SelectItem value="quote">Quote</SelectItem>
            </SelectContent>
          </Select>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-xs text-gray-500">
              <X className="w-3 h-3 mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-gray-200 hover:bg-transparent">
              <TableHead className="w-10"><BulkSelectCheckbox checked={selected.length === filteredLeads.length && filteredLeads.length > 0} onCheckedChange={() => setSelected(p => p.length === filteredLeads.length ? [] : filteredLeads.map(l => l.id))} testId="select-all-leads" /></TableHead>
              <TableHead className="text-gray-500">Name</TableHead>
              <TableHead className="text-gray-500 hidden sm:table-cell">Email</TableHead>
              <TableHead className="text-gray-500 hidden md:table-cell">Phone</TableHead>
              <TableHead className="text-gray-500 hidden md:table-cell">Type</TableHead>
              <TableHead className="text-gray-500 hidden lg:table-cell">Message</TableHead>
              <TableHead className="text-gray-500 hidden lg:table-cell">Pref. Makes</TableHead>
              <TableHead className="text-gray-500">Status</TableHead>
              <TableHead className="text-gray-500 hidden sm:table-cell">Date</TableHead>
              <TableHead className="text-gray-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="text-center text-gray-400 py-8">Loading...</TableCell></TableRow>
            ) : filteredLeads.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center text-gray-400 py-8">{hasActiveFilters ? 'No leads match your filters' : 'No leads yet'}</TableCell></TableRow>
            ) : filteredLeads.map(lead => (
              <TableRow key={lead.id} className="border-gray-200 text-gray-700">
                <TableCell><BulkSelectCheckbox checked={selected.includes(lead.id)} onCheckedChange={() => toggleSelect(lead.id)} testId={`select-lead-${lead.id}`} /></TableCell>
                <TableCell className="font-medium">{lead.name}</TableCell>
                <TableCell className="text-xs hidden sm:table-cell">{lead.email}</TableCell>
                <TableCell className="text-xs hidden md:table-cell">{lead.phone || '--'}</TableCell>
                <TableCell className="hidden md:table-cell"><Badge variant="outline" className="text-xs border-gray-300 text-gray-700 capitalize">{lead.type}</Badge></TableCell>
                <TableCell className="text-xs max-w-[150px] truncate hidden lg:table-cell">{lead.message || '--'}</TableCell>
                <TableCell className="hidden lg:table-cell">
                  <div className="flex flex-wrap gap-0.5">
                    {(lead.preferred_makes || []).length > 0
                      ? lead.preferred_makes.map(m => <Badge key={m} variant="outline" className="text-[10px] border-gray-300 text-gray-700">{m}</Badge>)
                      : <span className="text-xs text-gray-400">--</span>
                    }
                  </div>
                </TableCell>
                <TableCell>
                  <Select value={lead.status} onValueChange={(v) => updateStatus(lead.id, v)}>
                    <SelectTrigger className={`h-7 w-24 sm:w-28 text-xs border ${statusColors[lead.status] || ''}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="qualified">Qualified</SelectItem>
                      <SelectItem value="converted">Converted</SelectItem>
                      <SelectItem value="lost">Lost</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-xs text-gray-400 hidden sm:table-cell">{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '--'}</TableCell>
                <TableCell className="text-right">
                  {/* Desktop actions */}
                  <div className="hidden sm:flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setViewLead(lead)} title="View">
                      <Eye className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-amber-500" onClick={() => openEditLead(lead)} title="Edit">
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-500" onClick={() => { setSendOpen(lead); setSelectedUnits([]); }} title="Send Inventory">
                      <Send className="w-3 h-3" />
                    </Button>
                    {lead.status !== 'converted' && isAdmin && (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-500" onClick={() => handleConvert(lead)} title="Convert to Client">
                        <UserPlus className="w-3 h-3" />
                      </Button>
                    )}
                    {isAdmin && (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => handleDelete(lead.id)} title="Delete">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  {/* Mobile dropdown */}
                  <div className="sm:hidden">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`actions-menu-lead-${lead.id}`}><MoreVertical className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white">
                        <DropdownMenuItem onClick={() => setViewLead(lead)}><Eye className="w-3.5 h-3.5 mr-2" />View</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEditLead(lead)}><Pencil className="w-3.5 h-3.5 mr-2" />Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setSendOpen(lead); setSelectedUnits([]); }}><Send className="w-3.5 h-3.5 mr-2 text-blue-500" />Send Inventory</DropdownMenuItem>
                        {lead.status !== 'converted' && isAdmin && (
                          <DropdownMenuItem onClick={() => handleConvert(lead)}><UserPlus className="w-3.5 h-3.5 mr-2 text-green-500" />Convert to Client</DropdownMenuItem>
                        )}
                        {isAdmin && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDelete(lead.id)} className="text-red-500"><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* View Lead Dialog */}
      <Dialog open={!!viewLead} onOpenChange={() => setViewLead(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900">
          <DialogHeader><DialogTitle className="text-gray-900 uppercase">Lead Details</DialogTitle></DialogHeader>
          {viewLead && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="text-xs text-gray-500">Name</label><p className="font-medium">{viewLead.name}</p></div>
                <div><label className="text-xs text-gray-500">Email</label><p className="text-sm">{viewLead.email}</p></div>
                <div><label className="text-xs text-gray-500">Phone</label><p className="text-sm">{viewLead.phone || '--'}</p></div>
                <div><label className="text-xs text-gray-500">Type</label><Badge variant="outline" className="capitalize">{viewLead.type}</Badge></div>
              </div>
              {viewLead.message && (
                <div><label className="text-xs text-gray-500">Message</label><p className="text-sm text-gray-600">{viewLead.message}</p></div>
              )}
              {viewLead.preferred_makes?.length > 0 && (
                <div>
                  <label className="text-xs text-gray-500">Preferred Makes</label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {viewLead.preferred_makes.map(m => <Badge key={m} variant="outline" className="text-xs">{m}</Badge>)}
                  </div>
                </div>
              )}
              {viewLead.source === 'chatbot' && viewLead.chat_transcript_url && (
                <div className="pt-2 border-t border-gray-100">
                  <label className="text-xs text-gray-500">Chat Transcript</label>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-1 w-full border-[#E31937]/20 text-[#E31937] hover:bg-[#E31937]/5"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${viewLead.chat_transcript_url}`, '_blank')}
                    data-testid="download-transcript-btn"
                  >
                    <FileText className="w-3.5 h-3.5 mr-2" /> View Chat Transcript PDF
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Lead Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900">
          <DialogHeader><DialogTitle className="text-gray-900 uppercase">Add Lead</DialogTitle></DialogHeader>
          <form onSubmit={handleAdd} className="space-y-3">
            <Input placeholder="Name *" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="bg-gray-100 border-gray-200" />
            <Input placeholder="Email *" type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="bg-gray-100 border-gray-200" />
            <Input placeholder="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="bg-gray-100 border-gray-200" />
            <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
              <SelectTrigger className="bg-gray-100 border-gray-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="inquiry">Inquiry</SelectItem>
                <SelectItem value="viewing">Viewing Request</SelectItem>
                <SelectItem value="financing">Financing</SelectItem>
                <SelectItem value="quote">Quote</SelectItem>
              </SelectContent>
            </Select>
            <Textarea placeholder="Message" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} className="bg-gray-100 border-gray-200" rows={3} />
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="border-gray-300" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#E31937] text-white" disabled={saving}>{saving ? 'Adding...' : 'Add Lead'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Lead Dialog */}
      <Dialog open={!!editLead} onOpenChange={() => setEditLead(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900">
          <DialogHeader><DialogTitle className="text-gray-900 uppercase">Edit Lead</DialogTitle></DialogHeader>
          <form onSubmit={handleEditLead} className="space-y-3">
            <Input placeholder="Name *" required value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className="bg-gray-100 border-gray-200" data-testid="edit-lead-name" />
            <Input placeholder="Email *" type="email" required value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} className="bg-gray-100 border-gray-200" data-testid="edit-lead-email" />
            <Input placeholder="Phone" value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} className="bg-gray-100 border-gray-200" data-testid="edit-lead-phone" />
            <Select value={editForm.type} onValueChange={v => setEditForm(f => ({ ...f, type: v }))}>
              <SelectTrigger className="bg-gray-100 border-gray-200" data-testid="edit-lead-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="inquiry">Inquiry</SelectItem>
                <SelectItem value="viewing">Viewing Request</SelectItem>
                <SelectItem value="financing">Financing</SelectItem>
                <SelectItem value="quote">Quote</SelectItem>
              </SelectContent>
            </Select>
            <Textarea placeholder="Message" value={editForm.message} onChange={e => setEditForm(f => ({ ...f, message: e.target.value }))} className="bg-gray-100 border-gray-200" rows={3} data-testid="edit-lead-message" />
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="border-gray-300" onClick={() => setEditLead(null)}>Cancel</Button>
              <Button type="submit" className="bg-[#E31937] text-white" disabled={saving} data-testid="save-edit-lead-btn">{saving ? 'Saving...' : 'Save Changes'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send Inventory Dialog */}
      <Dialog open={!!sendOpen} onOpenChange={() => setSendOpen(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-gray-900 uppercase">Send Inventory to {sendOpen?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-500 mb-4">Select units to send to {sendOpen?.email}</p>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {inventory.map(item => (
              <div key={item.id} className="flex items-center gap-3 p-3 border border-gray-200 rounded hover:bg-gray-50 cursor-pointer" onClick={() => toggleUnit(item.id)}>
                <Checkbox checked={selectedUnits.includes(item.id)} />
                <img src={item.images?.[0] || '/placeholder.jpg'} alt="" className="w-16 h-12 object-cover rounded" />
                <div className="flex-1">
                  <p className="font-medium text-sm">{item.title || `${item.year} ${item.make} ${item.model}`}</p>
                  <p className="text-xs text-gray-500">${item.price?.toLocaleString() || 'Call'}</p>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2 mt-4">
            <Button type="button" variant="outline" className="border-gray-300" onClick={() => setSendOpen(null)}>Cancel</Button>
            <Button className="bg-[#E31937] text-white" disabled={saving || selectedUnits.length === 0} onClick={handleSendInventory}>
              {saving ? 'Sending...' : `Send ${selectedUnits.length} Unit(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkActionBar count={selected.length} onDelete={async () => {
        if (!window.confirm(`Delete ${selected.length} lead(s)?`)) return;
        try { await leadsAPI.bulkDelete(selected); toast.success(`${selected.length} lead(s) deleted`); setSelected([]); load(); } catch { toast.error('Failed'); }
      }} onClear={() => setSelected([])} label="lead(s)" />
    </div>
  );
}
